import React, { useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { AlineTextarea } from 'src/components';
import { composeRefs } from 'src/helpers/compose-refs';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

const DEBOUNCE_MS = 500;

const useStyles = makeStyles()({
  /**
   * `&&` beats AlineTextarea's default 1px border and padding so the field
   * reads as plain text. Focus is the caret, not a new outline.
   */
  borderless: {
    '&&': {
      '&:focus': {
        border: 'none',
        outline: 'none',
      },
      background: 'transparent',
      border: 'none',
      padding: 0,
    },
  },
});

interface PlaybookTextareaProps {
  onPersist: (value: string) => void;
  value: string;
  autoFocus?: boolean;
  className?: string;
  dataCy?: string;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLTextAreaElement>;
  maxRows?: number;
  onFocus?: () => void;
  placeholder?: string;
}

/**
 * Auto-growing textarea used by playbook description and position fields.
 * Mirrors an external value while idle and keeps a local draft so a mutation
 * does not fire on every keystroke. Persists after the last keystroke and
 * flushes on blur and unmount.
 */
export function PlaybookTextarea({
  autoFocus,
  className,
  dataCy,
  disabled,
  inputRef: externalInputRef,
  maxRows = 8,
  onFocus,
  onPersist,
  placeholder,
  value,
}: PlaybookTextareaProps): JSX.Element {
  const { classes } = useStyles();
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  /**
   * Mirrors the draft so blur reads the latest text even when the state update
   * that produced it hasn't rendered yet — Escape reverts and blurs in the same
   * tick, and without this the blur would persist the discarded edit.
   */
  const draftRef = useRef(value);
  /**
   * The last `value` the draft was synced from. Adoption is keyed off this
   * changing rather than off the effect firing, because a save can land while
   * the field is still focused — the mutation isn't optimistic, so `value` is
   * still the pre-edit text and syncing there would flash it back into the
   * field until the server responds.
   */
  const syncedValueRef = useRef(value);
  const persistedValueRef = useRef(value);
  persistedValueRef.current = value;
  const onPersistRef = useRef(onPersist);
  onPersistRef.current = onPersist;

  function updateDraft(next: string): void {
    draftRef.current = next;
    setDraft(next);
  }

  function persist(next: string = draftRef.current): void {
    if (next !== persistedValueRef.current) onPersistRef.current(next);
  }

  const debouncedPersist = useDebouncedCallback(persist, DEBOUNCE_MS);

  // Adopt external updates only while the field is idle, so a save landing
  // mid-edit doesn't clobber what the user is typing.
  useEffect(() => {
    if (value === syncedValueRef.current) return;
    syncedValueRef.current = value;
    if (!focused) updateDraft(value);
  }, [value, focused]);

  /**
   * use-debounce drops a queued call on unmount. Flush so navigating away
   * doesn't silently lose the last keystrokes.
   */
  useEffect(() => () => debouncedPersist.flush(), [debouncedPersist]);

  return (
    <AlineTextarea
      ref={composeRefs(inputRef, externalInputRef)}
      autoFocus={autoFocus}
      className={clsx(classes.borderless, className)}
      dataCy={dataCy}
      disabled={disabled}
      maxRows={maxRows}
      minRows={1}
      placeholder={placeholder}
      value={draft}
      onBlur={() => {
        setFocused(false);
        debouncedPersist.flush();
      }}
      onChange={event => {
        const next = event.target.value;
        updateDraft(next);
        debouncedPersist(next);
      }}
      onFocus={() => {
        setFocused(true);
        onFocus?.();
      }}
      onKeyDown={event => {
        if (event.key !== 'Escape') return;
        debouncedPersist.cancel();
        updateDraft(value);
        inputRef.current?.blur();
      }}
    />
  );
}
